import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ExchangeAccount } from '@/state/exchanges/exchangesSliceTypes';
import { EXCHANGES } from '@/lib/types/generalTypes';
import { fiatVolumesQuery } from '@/api/queries/fiatVolumesQuery';
import store from '@/store/store';
import {
    setApiKeyLoadingBulk,
    setGeneralLoadingStatus,
    setGeneralLoadingStatusBulk,
    setGlobalUnlockData,
    setKeysForLoading,
} from '@/state/general/generalSlice';
import { setStorage, setStorageLoading } from '@/state/storage/storageSlice';
import { DATA_LOADING_KEYS, LOADING_STATE } from '@/state/general/generalTypes';
import {
    getAllSettings,
    getConnectedTwitterData,
    getMemberProfile,
    getMenuOrder,
    getTelegramChats,
} from '@/api/settingsApi';
import {
    setTelegramData,
    setTwitterData,
    updateMemberProfile,
    updateSettings,
} from '@/state/member/memberSlice';
import { getActualSubscription } from '@/api/paymentsApi';
import { setSubscription } from '@/state/payments/paymentsSlice';
import websocket from '@/lib/websocket';
import { BROWSER_TAB, MEMBER_STATUS } from '@/state/member/memberTypes';
import { logoutMember } from '@/state/member/useMember';
import { getAllStorage } from '@/api/storageApi';
import { getAllBalances } from '@/api/balancesPositionsApi';
import { queryClient } from '@/lib/query/queryClient';
import { accountsQuery } from '@/api/queries/accountsQuery';
import { setAllMenuOrder } from '@/state/order/orderSlice';
import { getLeaderboardSettings } from '@/api/leaderboardApi';
import { updateLeaderboardSettings } from '@/state/leaderboard/leaderboardSlice';
import { getAllAlarms } from '@/api/intelligenceApi';
import { setAlarms } from '@/state/intelligence2/intelligence2Slice';
import { getClientStorage, getGlobalUnlockData } from '@/api/generalApi';
import { BalancesRow } from '@/store/syncStore/models';
import syncStoreService from '@/store/syncStore/SyncStoreService';
import { setClientStorageState } from '@/state/clientStorage/clientStorageSlice';
import { ClientStorage } from '@/state/clientStorage/clientStorageTypes';
import useDatePreset from '@/components/ExchangePageComponents/ComplexSelect/useDatePreset';
import { cloneDeep } from 'lodash';
import { DATE_PRESET_LABELS } from '@/state/legs/legsTypes';
import { setSentryUser } from '@/lib/sentry/sentry';

const useGetInitialData = (
    fetchOnStart = true,
): {
    initialFetch: () => Promise<void>;
    getAllFilters: () => Promise<Partial<ClientStorage>>;
} => {
    const dispatch = useDispatch();
    const { customRanges } = useDatePreset();
    // Web Worker POC
    // const worker = useMemo(() => {
    //     return new Worker(new URL('../workers/initialDataLoader.worker.ts', import.meta.url));
    // }, []);
    //
    // useEffect(() => {
    //     if (initial.current) {
    //         if (worker) {
    //             initial.current = false;
    //             worker.onmessage = ({ data: { answer } }) => {
    //                 console.log(answer);
    //                 return () => worker.terminate();
    //             };
    //         }
    //     }
    // }, [worker]);
    //
    // useEffect(() => {
    //     worker.postMessage({ question: 'why?' });
    // }, []);

    const getAllExchanges = async (): Promise<ExchangeAccount[]> => {
        try {
            const result = await queryClient.fetchQuery(accountsQuery);

            const ids = result.filter((item) => !item.keyError).map((item) => `${item.id}`);
            syncStoreService.setQueriesForBalancesByAccountId(ids);
            syncStoreService.setQueriesForPositionsByAccountId(ids);

            return result;
        } catch (error) {
            dispatch(setGeneralLoadingStatus({ key: 'exchanges', status: LOADING_STATE.ERROR }));
            throw error;
        }
    };

    const getStorage = async () => {
        try {
            const { data } = await getAllStorage();
            const payload: BalancesRow[] = data.map((item) => {
                return {
                    accountId: 'storage',
                    symbol: item.asset,
                    value: item.amount,
                    exchange: 'storage',
                    wallet: 'storage',
                    conversionId: `${item.asset.toLowerCase()}-usd-storage`,
                    storageId: item.id,
                };
            });
            syncStoreService.setBalanceRowsInTransaction(payload);
            return data;
        } catch (error) {
            dispatch(setGeneralLoadingStatus({ key: 'storage', status: LOADING_STATE.ERROR }));
            console.log(error);
            throw error;
        }
    };

    const normalizeDateFilters = (dateObj: {
        startDate: string;
        endDate: string;
        label: DATE_PRESET_LABELS;
    }) => {
        if (!dateObj)
            return {
                startDate: new Date('01/06/2016').toISOString(),
                endDate: new Date('01/06/2016').toISOString(),
                label: DATE_PRESET_LABELS.LAST_50_TRADES,
            };

        const customRange = customRanges.find((item) => item.label === dateObj.label);
        if (!customRange) {
            return dateObj;
        }
        const { startDate, endDate } = customRange.range();
        return {
            startDate: new Date(startDate || '').toISOString(),
            endDate: new Date(endDate || '').toISOString(),
            label: dateObj.label as DATE_PRESET_LABELS,
        };
    };

    const getAllFilters = async (): Promise<Partial<ClientStorage>> => {
        try {
            const filters = await getClientStorage();
            const metaData = cloneDeep(filters.data.metadata);

            if (metaData?.generalDateSelection) {
                metaData.generalDateSelection = normalizeDateFilters(metaData.generalDateSelection);
            }

            if (
                metaData?.accountDateSelection &&
                Object.keys(metaData?.accountDateSelection).length > 0
            ) {
                for (const accountId in metaData.accountDateSelection) {
                    metaData.accountDateSelection[accountId] = normalizeDateFilters(
                        metaData.accountDateSelection[accountId],
                    );
                }
            }

            return metaData;
        } catch (error) {
            console.error(error);
            return {};
        }
    };

    const initialFetch = async () => {
        try {
            dispatch(
                setGeneralLoadingStatusBulk({
                    keys: [
                        DATA_LOADING_KEYS.EXCHANGES,
                        DATA_LOADING_KEYS.FIAT,
                        DATA_LOADING_KEYS.STORAGE,
                    ],
                    status: LOADING_STATE.LOADING,
                }),
            );
            // Setup initial order of menus:
            const { data: getAccountsOrder } = await getMenuOrder();
            if (getAccountsOrder.menuOrder) {
                dispatch(setAllMenuOrder({ menuOrder: getAccountsOrder.menuOrder }));
            }
            const filters = await getAllFilters();
            if (filters) {
                dispatch(setClientStorageState(filters));
            }

            const accounts = await getAllExchanges();
            dispatch(setKeysForLoading(accounts));
            dispatch(
                setGeneralLoadingStatus({
                    key: DATA_LOADING_KEYS.EXCHANGES,
                    status: LOADING_STATE.SUCCESS,
                }),
            );
            dispatch(setStorageLoading(true));
            // [NEW WEBSOCKET]
            const token = store.getState()?.auth?.tokens?.accessToken || '';
            await websocket.connect();
            await websocket.authorize(token);
            await websocket.subscribeToFiatRates();

            if (accounts.length > 0) {
                const filteredAccounts = accounts
                    .filter((account) => !(account.exchange == EXCHANGES.FTX || account.keyError))
                    .map((account) => account.id);
                dispatch(
                    setApiKeyLoadingBulk({
                        apiKeys: filteredAccounts,
                        status: LOADING_STATE.LOADING,
                        loadingKey: 'balances',
                    }),
                );

                // TODO - remove this
                const badAccounts = accounts
                    .filter((account) => account.exchange === EXCHANGES.FTX || account.keyError)
                    .map((account) => account.id);

                dispatch(
                    setApiKeyLoadingBulk({
                        apiKeys: badAccounts,
                        status: LOADING_STATE.ERROR,
                        loadingKey: 'positions',
                    }),
                );
                dispatch(
                    setApiKeyLoadingBulk({
                        apiKeys: badAccounts,
                        status: LOADING_STATE.ERROR,
                        loadingKey: 'balances',
                    }),
                );
            }

            // [GET ALL BALANCES]
            await getAllBalances();

            const [profile, subscription, settings] = await Promise.all([
                getMemberProfile(),
                getActualSubscription(),
                getAllSettings(),
            ]);

            if (profile?.data?.id) {
                setSentryUser(profile.data.id);
            }

            if (profile.data?.status && profile.data.status !== MEMBER_STATUS.ACTIVE) {
                setSentryUser(null);
                await logoutMember(false);
                return;
            }

            dispatch(
                setGeneralLoadingStatusBulk({
                    keys: [DATA_LOADING_KEYS.FIAT, DATA_LOADING_KEYS.STORAGE],
                    status: LOADING_STATE.SUCCESS,
                }),
            );
            dispatch(updateMemberProfile(profile.data));
            dispatch(setSubscription(subscription?.data || null));

            // TODO remove after a full migration 90 days (± Sep 2026)
            const userSettings = { ...settings.data };
            if (Object.values(BROWSER_TAB).includes(userSettings.browserTabDisplay)) {
                userSettings.browserTabDisplay = BROWSER_TAB.totalNetWorthFiat;
            }
            // ------------------------------
            dispatch(updateSettings(settings.data));

            const [, storage] = await Promise.all([
                queryClient.fetchQuery(fiatVolumesQuery),
                getStorage(),
            ]);
            dispatch(setStorage(storage));
            dispatch(setStorageLoading(false));

            // Low-priority secondary data — fetched after critical data is loaded
            const [telegramConnection, twitterData, leaderboardSettings, alarms, globalUnlockData] =
                await Promise.all([
                    getTelegramChats(),
                    getConnectedTwitterData(),
                    getLeaderboardSettings(),
                    getAllAlarms(),
                    getGlobalUnlockData(),
                ]);

            dispatch(setTelegramData(telegramConnection.data[0] ?? null));
            dispatch(setTwitterData(twitterData?.data || null));
            dispatch(updateLeaderboardSettings(leaderboardSettings.data));
            dispatch(setAlarms(alarms.data || []));
            dispatch(setGlobalUnlockData(globalUnlockData.data || null));

            await websocket.subscribeToBalances();
            await websocket.subscribeToPositions();
        } catch (error) {
            // error modal and reload page suggestion
            console.error(error);
        }
    };

    useEffect(() => {
        if (fetchOnStart) {
            initialFetch();
        }

        return () => {
            websocket.disconnect();
        };
    }, []);

    return { initialFetch, getAllFilters };
};

export { useGetInitialData };
